import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, Car, DollarSign, CheckCircle, Loader2, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getCurrentSalvage, getSalvageStats, getMyBids } from "@/Calls/callSalvageAuth";

interface Stats { pending_bids: number; accepted_bids: number; rejected_bids: number; total_bids: number; available_vehicles: number; }
interface Company { company_name: string; email: string; }
interface Bid { id: number; vehicle_vin: string | null; bid_amount: number; status: string; created_at: string; }

const BID_STATUS: Record<string, string> = {
    pending:  "bg-yellow-100 text-yellow-700",
    accepted: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
};

export default function SalvageDashboardPage() {
    const [company, setCompany] = useState<Company | null>(null);
    const [stats,   setStats]   = useState<Stats | null>(null);
    const [bids,    setBids]    = useState<Bid[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentSalvage().then((r) => setCompany(r.data.company)),
            getSalvageStats().then((d)    => setStats(d.stats)),
            getMyBids().then((d)          => setBids((d.bids ?? []).slice(0, 8))),
        ])
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Salvage Dashboard</h1>
                <p className="text-muted-foreground text-sm mt-0.5">{company?.company_name}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: "Available Vehicles", value: stats?.available_vehicles ?? 0, icon: Car,         color: "text-blue-600",   bg: "bg-blue-50"   },
                    { label: "Bids Placed",         value: stats?.total_bids ?? 0,        icon: DollarSign,  color: "text-orange-600", bg: "bg-orange-50" },
                    { label: "Pending Bids",        value: stats?.pending_bids ?? 0,      icon: Archive,     color: "text-yellow-600", bg: "bg-yellow-50" },
                    { label: "Accepted Bids",       value: stats?.accepted_bids ?? 0,     icon: CheckCircle, color: "text-green-600",  bg: "bg-green-50"  },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">{value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            {/* Recent Bids */}
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h2 className="font-semibold">Recent Bids</h2>
                    <Link to="/salvage/bids" className="text-xs text-orange-600 hover:underline flex items-center gap-1">
                        View all <ArrowUpRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className="divide-y">
                    {bids.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No bids placed yet</p>
                    ) : bids.map((b) => (
                        <div key={b.id} className="flex items-center justify-between px-5 py-3 hover:bg-muted/40">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                                    <Car className="h-4 w-4 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">VIN: {b.vehicle_vin ?? "—"}</p>
                                    <p className="text-xs text-muted-foreground">
                                        Bid #{b.id} · {new Date(b.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-mono font-medium">
                                    ${Number(b.bid_amount).toLocaleString()}
                                </span>
                                <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${BID_STATUS[b.status] ?? "bg-gray-100 text-gray-600"}`}>
                                    {b.status.toUpperCase()}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Quick links */}
            <div className="grid grid-cols-2 gap-3">
                <Link to="/salvage/vehicles">
                    <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer">
                        <Car className="h-5 w-5 text-orange-600 shrink-0" />
                        <span className="text-sm font-medium">Browse Vehicles</span>
                    </Card>
                </Link>
                <Link to="/salvage/bids">
                    <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer">
                        <DollarSign className="h-5 w-5 text-green-600 shrink-0" />
                        <span className="text-sm font-medium">My Bids</span>
                    </Card>
                </Link>
            </div>
        </div>
    );
}
